import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useDemoState } from "@/data/demoState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HardHat, Users, Clock, AlertTriangle, CheckCircle2, ArrowRight, MapPin } from "lucide-react";

export default function OnSiteNowBoard({ projectId, compact }) {
  const { trades, tradeAttendance } = useDemoState();

  const onSiteTrades = useMemo(() => {
    const pid = projectId;
    const relevantTrades = pid ? trades.filter(t => t.projectId === pid) : trades;
    return relevantTrades.filter(t => t.status === 'checked-in' || t.status === 'cannot-start');
  }, [trades, projectId]);

  const totalCrew = useMemo(() => onSiteTrades.reduce((s, t) => s + (t.crewCount || 0), 0), [onSiteTrades]);
  const cannotStartCount = onSiteTrades.filter(t => t.status === 'cannot-start').length;

  if (onSiteTrades.length === 0 && compact) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="on-site-now-board">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs font-bold text-white uppercase tracking-wider">On-Site Now</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs font-bold text-white">{totalCrew} crew</span>
          </div>
          <div className="flex items-center gap-1.5">
            <HardHat className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs font-bold text-white">{onSiteTrades.length} trade{onSiteTrades.length !== 1 ? 's' : ''}</span>
          </div>
          {cannotStartCount > 0 && (
            <Badge className="text-[9px] bg-red-500/20 text-red-300 border-red-500/30">
              <AlertTriangle className="w-2.5 h-2.5 mr-1" />{cannotStartCount} blocked
            </Badge>
          )}
        </div>
      </div>

      {/* Trade rows */}
      {onSiteTrades.length === 0 ? (
        <div className="p-6 text-center">
          <HardHat className="w-8 h-8 text-slate-200 mx-auto mb-2" />
          <p className="text-xs text-slate-400">No trades on site right now</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {onSiteTrades.map(t => {
            const isBlocked = t.status === 'cannot-start';
            return (
              <div key={t.id} className={`px-4 py-3 flex items-center gap-3 ${isBlocked ? 'bg-red-50/50' : 'hover:bg-slate-50'} transition-colors`} data-testid={`onsite-trade-${t.id}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isBlocked ? 'bg-red-100' : 'bg-emerald-100'}`}>
                  <HardHat className={`w-4 h-4 ${isBlocked ? 'text-red-600' : 'text-emerald-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-800 truncate">{t.trade}</span>
                    <span className="text-[10px] text-slate-500">{t.company}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    {!compact && <span className="text-[10px] text-slate-500 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{t.project}</span>}
                    <span className="text-[10px] text-slate-500 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{t.checkInTime || '--:--'}</span>
                    <span className="text-[10px] text-slate-500 flex items-center gap-1"><Users className="w-2.5 h-2.5" />{t.crewCount || 0} crew</span>
                  </div>
                </div>
                {isBlocked ? (
                  <Badge className="text-[9px] bg-red-100 text-red-700 border-red-200">
                    <AlertTriangle className="w-2.5 h-2.5 mr-1" />Cannot Start
                  </Badge>
                ) : (
                  <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                    <CheckCircle2 className="w-2.5 h-2.5 mr-1" />Working
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      {!compact && (
        <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100">
          <Link to="/builder/trade-release">
            <Button variant="ghost" size="sm" className="text-[10px] text-slate-500 hover:text-slate-700 h-6 px-2">
              View Trade Release Board <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
